import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Checkbox,
  MenuItem,
  Grid,
  Fab,
  Select,
  ButtonBase,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Assignment,
  RateReview,
  CheckCircleOutline,
  EditNote,
  FileDownloadOutlined as ExportIcon,
} from "@mui/icons-material";
import { BRAND } from "../../components/auth/authTheme";
import "../../styles/layout.css";

const NAVY = BRAND.navy;
const ORANGE = BRAND.orangeHover;
const BG = "#F8FAFC";

const getRowKey = (row) => row.id ?? row.employee_id;

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft", className: "draft" },
  { value: "in_review", label: "In Review", className: "in_review" },
  { value: "approved", label: "Approved", className: "approved" },
  { value: "rejected", label: "Rejected", className: "rejected" },
  { value: "applied", label: "Applied to Payroll", className: "applied" },
];

const TabPill = ({ label, isActive, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      flex: 1,
      py: 1,
      borderRadius: "10px",
      fontWeight: 600,
      fontSize: "0.8125rem",
      color: isActive ? ORANGE : BRAND.muted,
      bgcolor: isActive ? BRAND.orangeLight : "transparent",
      border: `1px solid ${isActive ? ORANGE : BRAND.border}`,
      transition: "all 0.15s",
    }}
  >
    {label}
  </ButtonBase>
);

const StatCard = ({ icon, label, value, iconBg, iconColor }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      px: 0.75,
      py: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: 0.5,
      border: `1px solid ${BRAND.border}`,
      height: "100%",
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "8px",
        bgcolor: iconBg,
        color: iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: NAVY, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.625rem",
        color: BRAND.muted,
        fontWeight: 500,
        lineHeight: 1.25,
        whiteSpace: "normal",
        wordBreak: "break-word",
        width: "100%",
      }}
    >
      {label}
    </Typography>
  </Box>
);

const AppraisalCard = ({
  cycle,
  isSelected,
  onToggleSelect,
  onView,
  onEdit,
  onDelete,
  canEditStatus,
  onStatusChange,
}) => {
  const period =
    cycle.cycle_start && cycle.cycle_end
      ? `${cycle.cycle_start} – ${cycle.cycle_end}`
      : "N/A";
  const statusMeta = STATUS_OPTIONS.find((s) => s.value === cycle.status);

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "12px",
        mb: 1.25,
        border: isSelected ? `2px solid ${ORANGE}` : `1px solid ${BRAND.border}`,
        boxShadow: "0 1px 6px rgba(26, 43, 74, 0.04)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 1.25, py: 0.875 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "24px 1fr auto auto auto",
            alignItems: "center",
            columnGap: 0.5,
            mb: 0.35,
          }}
        >
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={() => onToggleSelect(cycle)}
            sx={{
              p: 0,
              color: BRAND.muted,
              "&.Mui-checked": { color: ORANGE },
            }}
            inputProps={{ "aria-label": `Select ${cycle.employee_name}` }}
          />
          <Typography
            sx={{
              fontWeight: 700,
              color: NAVY,
              fontSize: "0.875rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {cycle.employee_name || "Unknown"}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onView(cycle)}
            aria-label="View appraisal"
            sx={{ p: 0, color: "#2196f3", width: 32, height: 32 }}
          >
            <VisibilityIcon sx={{ fontSize: "1.05rem" }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onEdit(cycle)}
            aria-label="Edit appraisal"
            sx={{ p: 0, color: ORANGE, width: 32, height: 32 }}
          >
            <EditIcon sx={{ fontSize: "1.05rem" }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDelete(cycle.id)}
            aria-label="Delete appraisal"
            sx={{ p: 0, color: "#d32f2f", width: 32, height: 32 }}
          >
            <DeleteIcon sx={{ fontSize: "1.05rem" }} />
          </IconButton>
        </Box>

        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 600,
            color: ORANGE,
            pl: "calc(24px + 6px)",
            mb: 0.5,
          }}
        >
          {period}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
            pl: "calc(24px + 6px)",
            mb: 0.75,
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted }}>
            Hike:{" "}
            <strong style={{ color: NAVY }}>
              {cycle.hike_percentage != null ? `${cycle.hike_percentage}%` : "N/A"}
            </strong>
          </Typography>
          <Typography sx={{ fontSize: "0.6875rem", color: BRAND.border }}>
            ·
          </Typography>
          <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted }}>
            Reviews:{" "}
            <strong style={{ color: NAVY }}>{cycle.review_count ?? 0}</strong>
          </Typography>
        </Box>

        {canEditStatus ? (
          <Box sx={{ pl: "calc(24px + 6px)" }}>
            <Select
              size="small"
              fullWidth
              value={cycle.status || "draft"}
              onChange={(e) => onStatusChange(cycle, e.target.value)}
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                borderRadius: "8px",
                bgcolor: BRAND.bg,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: BRAND.border },
              }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        ) : (
          <Box sx={{ pl: "calc(24px + 6px)" }}>
            <span
              className={`appraisal-status-badge ${statusMeta?.className || "draft"}`}
            >
              {statusMeta?.label || cycle.status}
            </span>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const HistoryCard = ({ employee, isSelected, onToggleSelect, onView }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      mb: 1.25,
      border: isSelected ? `2px solid ${ORANGE}` : `1px solid ${BRAND.border}`,
      boxShadow: "0 1px 6px rgba(26, 43, 74, 0.04)",
      overflow: "hidden",
    }}
  >
    <Box sx={{ px: 1.25, py: 0.875 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "24px 1fr 32px",
          alignItems: "center",
          columnGap: 0.75,
          mb: 0.35,
        }}
      >
        <Checkbox
          size="small"
          checked={isSelected}
          onChange={() => onToggleSelect(employee)}
          sx={{
            p: 0,
            color: BRAND.muted,
            "&.Mui-checked": { color: ORANGE },
          }}
          inputProps={{ "aria-label": `Select ${employee.employee_name}` }}
        />
        <Typography
          sx={{
            fontWeight: 700,
            color: NAVY,
            fontSize: "0.875rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {employee.employee_name || "Unknown"}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onView(employee)}
          aria-label="View history"
          sx={{
            p: 0,
            color: "#2196f3",
            bgcolor: "rgba(33, 150, 243, 0.08)",
            width: 32,
            height: 32,
          }}
        >
          <VisibilityIcon sx={{ fontSize: "1.05rem" }} />
        </IconButton>
      </Box>
      <Typography
        sx={{
          fontSize: "0.6875rem",
          fontWeight: 600,
          color: ORANGE,
          pl: "calc(24px + 6px)",
          mb: 0.25,
        }}
      >
        {employee.employee_id || "N/A"}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.6875rem",
          color: BRAND.muted,
          pl: "calc(24px + 6px)",
        }}
      >
        {employee.designation || "No designation"}
      </Typography>
    </Box>
  </Box>
);

const EmptyState = ({ message, hint, icon: Icon = Assignment }) => (
  <Box
    sx={{
      textAlign: "center",
      py: 6,
      px: 2,
      bgcolor: "#fff",
      borderRadius: "16px",
      border: `1px solid ${BRAND.border}`,
    }}
  >
    <Icon sx={{ fontSize: 48, color: "#CBD5E1", mb: 1 }} />
    <Typography sx={{ fontWeight: 600, color: NAVY, mb: 0.5 }}>
      {message}
    </Typography>
    <Typography sx={{ fontSize: "0.875rem", color: BRAND.muted }}>
      {hint}
    </Typography>
  </Box>
);

const AppraisalMobileView = ({
  activeTab,
  setActiveTab,
  appraisals,
  history,
  stats,
  searchTerm,
  setSearchTerm,
  selectedRows,
  onToggleSelect,
  onView,
  onEdit,
  onDelete,
  onHistoryView,
  onAdd,
  onExport,
  onClearSelection,
  canEditStatus,
  onStatusChange,
}) => {
  const selectedIds = useMemo(
    () => new Set(selectedRows.map((row) => getRowKey(row))),
    [selectedRows]
  );

  const subtitle =
    activeTab === 0
      ? "Manage employee appraisal cycles, hikes, and review status"
      : "Browse historical appraisal records by employee";

  return (
    <Box sx={{ pb: 10, pt: 1, px: 2, bgcolor: BG, minHeight: "100vh" }}>
      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: NAVY, mb: 0.25 }}>
        Appraisal Cycle
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: BRAND.muted, mb: 1.5 }}>
        {subtitle}
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
        <TabPill
          label="Appraisals"
          isActive={activeTab === 0}
          onClick={() => setActiveTab(0)}
        />
        <TabPill
          label="History"
          isActive={activeTab === 1}
          onClick={() => setActiveTab(1)}
        />
      </Box>

      {activeTab === 0 ? (
        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          <Grid item xs={6} sx={{ minWidth: 0 }}>
            <StatCard
              icon={<Assignment sx={{ fontSize: "1rem" }} />}
              label="Total Cycles"
              value={stats.total}
              iconBg="rgba(33,150,243,0.15)"
              iconColor="#2196f3"
            />
          </Grid>
          <Grid item xs={6} sx={{ minWidth: 0 }}>
            <StatCard
              icon={<RateReview sx={{ fontSize: "1rem" }} />}
              label="In Review"
              value={stats.inReview}
              iconBg="rgba(0,188,212,0.15)"
              iconColor="#00bcd4"
            />
          </Grid>
          <Grid item xs={6} sx={{ minWidth: 0 }}>
            <StatCard
              icon={<CheckCircleOutline sx={{ fontSize: "1rem" }} />}
              label="Approved"
              value={stats.approved}
              iconBg="rgba(76,175,80,0.15)"
              iconColor="#4caf50"
            />
          </Grid>
          <Grid item xs={6} sx={{ minWidth: 0 }}>
            <StatCard
              icon={<EditNote sx={{ fontSize: "1rem" }} />}
              label="Draft"
              value={stats.draft}
              iconBg="rgba(244,67,54,0.12)"
              iconColor="#f44336"
            />
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          <Grid item xs={12} sx={{ minWidth: 0 }}>
            <StatCard
              icon={<Assignment sx={{ fontSize: "1rem" }} />}
              label="Employees with History"
              value={stats.historyCount}
              iconBg="rgba(33,150,243,0.15)"
              iconColor="#2196f3"
            />
          </Grid>
        </Grid>
      )}

      {selectedRows.length > 0 && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.25,
            bgcolor: BRAND.orangeLight,
            borderRadius: "12px",
            border: `1px solid ${ORANGE}33`,
          }}
        >
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: NAVY, mb: 1 }}>
            {selectedRows.length} selected — export or clear
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box
              component="button"
              type="button"
              onClick={onExport}
              sx={{
                flex: 1,
                border: "none",
                borderRadius: "10px",
                bgcolor: ORANGE,
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.75rem",
                py: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              <ExportIcon sx={{ fontSize: "1rem" }} />
              Export
            </Box>
            <Box
              component="button"
              type="button"
              onClick={onClearSelection}
              sx={{
                flex: 1,
                border: `1px solid ${BRAND.border}`,
                borderRadius: "10px",
                bgcolor: "#fff",
                color: NAVY,
                fontWeight: 600,
                fontSize: "0.75rem",
                py: 1,
                cursor: "pointer",
              }}
            >
              Clear
            </Box>
          </Box>
        </Box>
      )}

      <TextField
        fullWidth
        placeholder={activeTab === 0 ? "Search appraisals..." : "Search history..."}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            bgcolor: "#fff",
            borderRadius: "12px",
            "& fieldset": { borderColor: BRAND.border },
            "&.Mui-focused fieldset": { borderColor: ORANGE },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: BRAND.muted }} />
            </InputAdornment>
          ),
        }}
      />

      {activeTab === 0 ? (
        appraisals.length > 0 ? (
          appraisals.map((cycle) => (
            <AppraisalCard
              key={cycle.id}
              cycle={cycle}
              isSelected={selectedIds.has(cycle.id)}
              onToggleSelect={onToggleSelect}
              onView={onView}
              onEdit={onEdit}
              onDelete={onDelete}
              canEditStatus={canEditStatus}
              onStatusChange={onStatusChange}
            />
          ))
        ) : (
          <EmptyState
            message="No appraisal cycles yet"
            hint='Tap + to create the first cycle.'
          />
        )
      ) : history.length > 0 ? (
        history.map((employee) => (
          <HistoryCard
            key={getRowKey(employee)}
            employee={employee}
            isSelected={selectedIds.has(getRowKey(employee))}
            onToggleSelect={onToggleSelect}
            onView={onHistoryView}
          />
        ))
      ) : (
        <EmptyState
          message="No appraisal history found"
          hint="History appears once employees have completed appraisal cycles."
        />
      )}

      {activeTab === 0 && (
        <Fab
          color="primary"
          aria-label="Add appraisal"
          onClick={onAdd}
          size="medium"
          sx={{
            position: "fixed",
            bottom: 'calc(90px + env(safe-area-inset-bottom, 0px))',
            right: 20,
            bgcolor: ORANGE,
            "&:hover": { bgcolor: BRAND.orange },
            boxShadow: "0 4px 16px rgba(230, 90, 0, 0.35)",
            zIndex: 1002,
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default AppraisalMobileView;
